import React, { memo, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { formGroup, formControl, FormDataType, formArray } from '@core/utils/form';
import { useNonNilObservable, useObservable } from '@core/utils/hooks/rxjs';
import { useConfigureBlock } from '@modules/course/CourseBuilder/hooks';
import { Container } from 'reactstrap';
import { css } from '@emotion/react';
import { useCourseBuilderContext } from '@modules/course/CourseBuilder/CourseBuilderPage';
import { GlossaryBlockSettings } from './GlossaryBlockSettings';
import { defineBlockAdvanceForm } from '../Common/AdvanceSettings';
import { CourseBuilder } from '../types';
import { BlockContainer } from '../BlockContainer';
import { GlossaryAPI } from '@business/api/glossary';
import { tap } from 'rxjs/operators';
import { FormHTMLInline } from '@core/components/form';
import { GlossaryBlockBloc } from './GlossaryBlockBloc';
import { useBloc } from '@core/utils/bloc';
import { HTMLService } from '@business/services';

export function defineDefinitionForm() {
  return formGroup({
    option: formControl<boolean>({}),
    definitionId: formControl<string>({}),
  });
}

function defineGlossaryForm(variation: GlossaryBlockVariation) {
  return formGroup({
    heading: formControl<string>({ initialValue: '' }),
    text: formControl<string>({
      initialValue: `<ol><li>Vel magna iaculis arcu cursus parturient blandit suscipit taciti auctor maecenas.</li><li>Penatibus libero risus montes conubia pretium quisque ut fermentum, vulputate augue litora ornare fames turpis convallis imperdiet, curae etiam sed enim dictumst purus sit.</li><li>Tortor parturient gravida habitant dapibus et fusce felis nam nibh litora ridiculus aenean porttitor turpis pharetra.</li><li>Suscipit montes natoque aptent ex gravida orci ultrices volutpat nunc.</li></ol>`,
    }),
    search: formControl<string>({
      placeholder: 'Search...',
      initialValue: '',
    }),
    searchValue: formControl<string>({
      initialValue: '',
    }),
    definitions: formArray(() => defineDefinitionForm()),
  });
}

function defineGlossaryBlockForm(variation: GlossaryBlockVariation) {
  return formGroup({
    glossaryTitle: formControl<string>({
      label: 'Glossary Title',
      initialValue: 'Definitions',
    }),
    blockHeading: formControl<string>({ initialValue: 'Definitions' }),
    glossary: formArray(() => defineGlossaryForm(variation)),
    advance: defineBlockAdvanceForm({ paddingTop: 0, paddingBottom: 0 }),
  });
}

export type GlossaryBlockWidgetFormData = FormDataType<typeof defineGlossaryBlockForm>;
export type GlossaryBlockWidgetForm = ReturnType<typeof defineGlossaryBlockForm>;

export type GlossaryForm = ReturnType<typeof defineGlossaryForm>;
export type GlossaryFormData = FormDataType<typeof defineGlossaryForm>;

export type DefinitionForm = ReturnType<typeof defineDefinitionForm>;
export type DefinitionFormData = FormDataType<typeof defineDefinitionForm>;

export enum GlossaryBlockVariation {
  glossary = 'glossary',
}

export const GlossaryBlock = memo((props: CourseBuilder.BlockProps<GlossaryBlockVariation>) => {
  const { variation, preview, editable } = props;
  const bloc = useBloc(GlossaryBlockBloc);
  const form = useMemo(() => defineGlossaryBlockForm(variation), [variation]);
  const glossary = useObservable(form.controls.glossary.items$, []);
  const hasContent = useMemo(() => (glossary.length == 0 ? false : true), [glossary]);

  useEffect(() => {
    if (!props.preview && glossary.length === 0) {
      form.controls.glossary.push();
    }
  }, [form, glossary, props.preview]);

  useConfigureBlock({
    props,
    form,
    contentComponent: GlossaryBlockSettings,
    contentProps: { form, variation, id: props.id, lessonId: props.lessonId, bloc },
  });

  return (
    <Container className="mt-2">
      <h1 className="py-4">
        <FormHTMLInline control={form.controls.glossaryTitle} readonly={!editable} />
      </h1>
      {!hasContent && (
        <React.Fragment>
          <h3 className="font-weight-bold px-4">Lorem Ipsum</h3>
          <p className="px-4">1. Vel magna iaculis arcu cursus parturient blandit suscipit taciti auctor maecenas.</p>
        </React.Fragment>
      )}
      {hasContent &&
        glossary.map((controls, index) => {
          return <GlossaryItem key={index} ctrl={controls} index={index} variation={variation} form={form} />;
        })}
    </Container>
  );
});

interface GlossaryItemProps {
  ctrl: GlossaryForm;
  index: number;
  variation: GlossaryBlockVariation;
  form: GlossaryBlockWidgetForm;
}

const GlossaryItem = memo((props: GlossaryItemProps) => {
  const { ctrl, index, variation, form } = props;
  const formValue = useNonNilObservable(ctrl.value$);
  const proposal = useCourseBuilderContext();
  const primaryColor = useMemo(() => proposal?.form.value.primaryColor, [proposal]);
  const parentFormValue = useNonNilObservable(form.value$);
  const glossaryItems = useObservable(ctrl.controls.definitions.items$, []);

  const listCss = useMemo(() => {
    if (primaryColor) {
      return css`
        li:before {
          background-color: ${primaryColor};
        }
      `;
    } else {
      return css`
        li:before {
          background-color: #45a8f7;
        }
      `;
    }
  }, [primaryColor]);

  return (
    <BlockContainer className={classNames('d-flex flex-fill', 'block-list', variation, 'px-4', 'py-1')} advanceForm={parentFormValue.advance}>
      <div css={listCss} key={`text-list-${index}`}>
        <h3 className="font-weight-bold pl-4">{formValue.heading}</h3>
        <ol>
          {glossaryItems.map((controls, index) => {
            return <GlossaryItemDefiniton key={index} ctrl={controls} index={index} definitons={formValue} />;
          })}
        </ol>
      </div>
    </BlockContainer>
  );
});

interface GlossaryItemDefinitonProps {
  index: number;
  ctrl: DefinitionForm;
  definitons: GlossaryFormData;
}

const GlossaryItemDefiniton = memo((props: GlossaryItemDefinitonProps) => {
  const { ctrl, index, definitons } = props;
  const formValue = useNonNilObservable(ctrl.value$);
  const [definitionText, setDefinitionText] = useState<string>('');
  const [length, setLength] = useState<number>(0);

  useEffect(() => {
    setLength(definitons.definitions.reduce((count, obj) => (obj.option === true ? count + 1 : count), 0));
  }, [definitons.definitions]);

  const definition = useMemo(() => {
    return GlossaryAPI.fetchGlossaryDefinition(formValue.definitionId)
      .pipe(tap(data => setDefinitionText(data.body)))
      .subscribe();
  }, [formValue.definitionId]);

  return (
    <>
      {formValue.option && length == 1 && <p className="p-0">{HTMLService.toReact(definitionText || '<p></p>')}</p>}
      {formValue.option && length > 1 && <li className="p-0">{HTMLService.toReact(definitionText || '<p></p>')}</li>}
    </>
  );
});
